import { useMemo } from "react";
import { activeTheme, useTheme } from "@/lib/theme";

/* Folder-style tabs: each tab gets its own hue and the open panel is framed in that colour, like a tabbed file folder.
   Hues come from the user's active palette (theme menu); extra tabs get complementary hues so neighbours stay distinct. */
const DEFAULT_HUES = [40, 200, 345, 150, 262, 22, 176, 230];
const MIN_GAP = 38; // degrees between any two folder hues

const parse = (c) => {
  const m = /^#([0-9a-f]{6})$/i.exec(c?.trim() || "");
  if (m) { const n = parseInt(m[1], 16); return [n >> 16, (n >> 8) & 255, n & 255]; }
  const r = /rgba?\(\s*(\d+)\D+(\d+)\D+(\d+)/i.exec(c || "");
  return r ? [+r[1], +r[2], +r[3]] : null;
};

const hsl = ([r, g, b]) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2, d = max - min;
  if (!d) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return { h: h * 60, s, l };
};

const gap = (a, b) => { const d = Math.abs(a - b) % 360; return d > 180 ? 360 - d : d; };

/* Pick `count` hues: palette hues first, then their complements/triads, each kept MIN_GAP° away from the ones already chosen. */
export const pickHues = (colors, count) => {
  const seeds = (colors || []).map(parse).filter(Boolean).map(hsl).filter((c) => c.s > 0.14 && c.l > 0.08 && c.l < 0.95).map((c) => c.h);
  const base = seeds.length ? seeds : DEFAULT_HUES;
  const candidates = [...base, ...base.map((h) => h + 180), ...base.map((h) => h + 120), ...base.map((h) => h + 240), ...base.map((h) => h + 60), ...base.map((h) => h + 300)].map((h) => ((h % 360) + 360) % 360);
  const out = [];
  for (let i = 0; i < count; i++) {
    let best = null, bestGap = -1;
    for (const h of candidates) {
      const g = out.length ? Math.min(...out.map((o) => gap(o, h))) : 999;
      if (g >= MIN_GAP) { best = h; break; }
      if (g > bestGap) { bestGap = g; best = h; }
    }
    out.push(best ?? DEFAULT_HUES[i % DEFAULT_HUES.length]);
  }
  return out;
};

export const hueSet = (h) => ({
  name: `h${Math.round(h)}`,
  border: `hsl(${h} 62% 52%)`,
  tab: `hsl(${h} 70% 90%)`,
  bg: `hsl(${h} 60% 97%)`,
  text: `hsl(${h} 55% 24%)`,
  badge: `hsl(${h} 62% 52% / 0.22)`,
  deep: `hsl(${h} 58% 30%)`,
  solid: `hsl(${h} 64% 46%)`,
  line: `hsl(${h} 55% 45%)`,
  tint: (a) => `hsl(${h} 64% 50% / ${a})`,
});

export const useFolderHues = (count) => {
  const theme = activeTheme(useTheme());
  const key = (theme?.colors || []).join("|");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => pickHues(theme?.colors, count).map(hueSet), [key, count]);
};

/* Classic tabbed file folder: tabs on top, the open folder framed below; translucent tints of the user's palette hues. */
export const FolderTabs = ({ tabs, value, onChange, testid = "folder-tabs", children }) => {
  const hues = useFolderHues(tabs.length);
  const idx = Math.max(0, tabs.findIndex((t) => t.id === value));
  const hue = hues[idx] || hueSet(DEFAULT_HUES[0]);
  return (
    <div data-testid={testid}>
      <div role="tablist" className="flex flex-wrap items-end gap-1 px-2" data-testid={`${testid}-list`}>
        {tabs.map((t, i) => {
          const h = hues[i];
          const on = i === idx;
          return (
            <button key={t.id} type="button" role="tab" aria-selected={on} data-testid={`${testid}-tab-${t.id}`} data-state={on ? "active" : "inactive"} data-hue={h.name}
              onClick={() => onChange(t.id)}
              style={{ background: h.tint(0.1), borderColor: h.tint(0.1), color: h.text }}
              className={`relative flex items-center gap-2 rounded-t-xl border-2 border-b-0 px-4 text-sm font-medium backdrop-blur-sm transition-all duration-150 ${
                on ? "z-10 -mb-0.5 h-11 pb-0.5" : "h-10 opacity-85 hover:-translate-y-0.5 hover:opacity-100"}`}>
              {t.icon && <t.icon size={13} strokeWidth={2} />}
              <span className="truncate">{t.label}</span>
              {t.badge !== undefined && (
                <span className="rounded px-1.5 py-0.5 font-mono text-[10px]" style={{ background: h.tint(0.16) }} data-testid={`${testid}-badge-${t.id}`}>{t.badge}</span>
              )}
              {on && <span aria-hidden className="absolute inset-x-0 -bottom-0.5 h-1" style={{ background: h.tint(0.1) }} />}
            </button>
          );
        })}
      </div>
      <div role="tabpanel" data-testid={`${testid}-panel`} data-hue={hue.name} style={{ borderColor: hue.tint(0.1), background: hue.tint(0.1) }}
        className="rounded-xl border-2 p-4 backdrop-blur-sm transition-colors duration-200 sm:p-5">
        {children(tabs[idx]?.id)}
      </div>
    </div>
  );
};
